// Customer Accounts dialogs (C22, D03 §1–§2), extracted from original/src/App.tsx (ADR-08 §2.1):
//   CustomersHeaderActions  :26307-26360  (debts header: "إضافة جديد" + internal operator chat)
//   AddCustomerModal        :47513-47840  (add debt / trust account)
//   DuplicateNameModal      :47840-48080  (same-name prompt)
//   EditCustomerModal       :34516-34623
//   CustomerSettleModal     :34625-34710
//   CustomerAdjustModal     :34712-35172  (incl. the surplus step :34720-34950 → `SurplusOptions`, shared with the transfer)
//   CustomerConvertModal    :35174-35368
//   ArchiveBlockedModal     :34271-34514  ("cannot delete" dialog)
//   ArchivedCustomersModal  — new, composed from the vocabulary of the dialogs above (D-072 "Archived accounts" list)
// Kept: frames, headers, labels, inputs, buttons and their classes, icons, the emoji of the old labels, the motion
// props. Cut everywhere: `localDataService`, the handlers (`handleAddDebt`, `handleAdjustDebt`, `handleSettleDebt`,
// `handleConfirmCurrencyConversion`, `handleUpdateDebt`, `handleSettleAndRetrieveDeleteCustomer`, …) → callbacks;
// `formatNumber` → preformatted labels; `getRealTimeBalanceDetails` (client arithmetic on stored balances) → `live`
// from the server preview; the wallet lists (`sortedWallets`, `wallets.find(w => w.type === "vault")`,
// `subSafeBalances`, the dollar-vault lookup by id 11 / name) → `boxOptions` chosen by the container per currency
// (D-029, D-118); `new Date()` due-date shortcuts → `onDueShortcut` (office clock, D-064); `showExchangeRate`
// (per browser) → `usdHidden` (D-066); `debts.filter(...)` target lists → `surplusTargets`.
// Changed by decision (each a V-entry in tests/visual/DEVIATIONS.md):
//   - DuplicateNameModal lists every account with the name (a person may hold a debt and a trust account, D-017) and
//     offers "open the other kind for the same person" and "restore" (D-072) when the container passes them (V-21);
//   - CustomerAdjustModal / transfer: "same customer" posts to the person's opposite account, created with the PIN
//     when missing (D-018); the existing-account picker lists accounts of the target kind in the same currency (V-22);
//   - CustomerConvertModal: the amount is the USD amount in both directions (engine input, D-012 — no client
//     division), the result, both new balances and (sell-type) the profit come from the server preview, a cost field
//     pre-filled with the average cost appears on sell-type conversions (D-070 (3), D-071, D-239), and the "super code"
//     line names the personal PIN (D-018) (V-23);
//   - ArchiveBlockedModal: the one-click "settle and delete" option is removed (D-072); the dialog says to settle
//     first and offers the ordinary settlement and the transfer (V-24).
import type { FormEvent } from 'react';
import { motion } from 'motion/react';
import {
  AlertTriangle, Archive, ArrowLeftRight, Bell, Coins, Edit2, FileText, MessageSquare, Plus, RotateCcw, ShieldCheck, X,
} from 'lucide-react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { NumericInput } from '../components/NumericInput.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { WalletSelect, type WalletSelectOption } from '../components/WalletSelect.js';
import { useI18n } from '../i18n-context.js';

export type CustomerKindKey = 'debt' | 'trust';
export type CustomerCurrency = 'IQD' | 'USD';

// ---------------------------------------------------------------------------------------------------------------
// Header actions (App.tsx:26307-26360)
// ---------------------------------------------------------------------------------------------------------------

export interface CustomersHeaderActionsProps {
  readonly themeColor: string;
  readonly unreadChatCount: number;
  readonly onAdd: () => void;
  readonly onChat: () => void;
}

export function CustomersHeaderActions(props: CustomersHeaderActionsProps) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2" data-testid="customers-header-actions">
      <button
        type="button"
        data-testid="customers-add"
        onClick={props.onAdd}
        style={{ backgroundColor: props.themeColor }}
        className="text-white px-5 py-2 rounded-xl flex items-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-blue-600/20 font-black tracking-tight cursor-pointer active:scale-95"
      >
        <Plus
          size={18}
          className="filter drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]"
        />
        <span className="text-xs sm:text-sm">{t('add')}</span>
      </button>

      <button
        type="button"
        onClick={props.onChat}
        className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 p-2 sm:px-3 sm:py-2 rounded-xl flex items-center gap-1.5 transition-all shadow-sm font-black tracking-tight cursor-pointer active:scale-95 relative"
        title={t('cust.chat_title')}
      >
        <MessageSquare size={17} />
        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 hidden sm:inline">
          {t('cust.chat_label')}
        </span>
        {props.unreadChatCount > 0 ? (
          <span className="min-w-[16px] h-[16px] bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-1 shadow-sm animate-bounce">
            {props.unreadChatCount}
          </span>
        ) : (
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Add account (App.tsx:47513-47840)
// ---------------------------------------------------------------------------------------------------------------

export interface AddCustomerValues {
  readonly name: string;
  readonly accountNumber: string;
  readonly phone: string;
  readonly address: string;
  readonly currency: CustomerCurrency;
  readonly amount: string;
  readonly kind: CustomerKindKey;
  readonly boxId: string;
  readonly note: string;
  /** YYYY-MM-DD or "". */
  readonly dueDate: string;
}

export interface AddCustomerModalProps {
  readonly values: AddCustomerValues;
  readonly onChange: (patch: Partial<AddCustomerValues>) => void;
  /** Old: the status select is disabled unless the list filter is "all" (`debtFilter !== "all"`). */
  readonly kindLocked: boolean;
  /** Old `walletOpts` (vault / operator's custody + digital boxes) for the chosen currency, labels + balances preformatted. */
  readonly boxOptions: SelectOption[];
  readonly usdHidden: boolean;
  readonly themeColor: string;
  readonly busy?: boolean;
  readonly onDueShortcut: (days: number) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

export function AddCustomerModal(props: AddCustomerModalProps) {
  const { t } = useI18n();
  const newDebt = props.values;
  const set = props.onChange;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!props.busy) props.onSubmit();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-add-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-xl sm:max-w-2xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[calc(100vh-2rem)] md:max-h-[85vh] flex flex-col"
      >
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-rose-50 dark:bg-rose-900/10 shrink-0">
          <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
            <Plus className="text-rose-600 dark:text-rose-400" />
            {t('cust.add_title')}
          </h3>
          <button
            onClick={props.onClose}
            type="button"
            className="text-gray-400 hover:text-gray-650 dark:hover:text-[#f0f6fc] transition-colors p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-[#30363d]"
          >
            <X size={20} />
          </button>
        </div>
        <form
          onSubmit={submit}
          className="flex-1 flex flex-col min-h-0 overflow-hidden text-right"
          dir="rtl"
        >
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 pr-2">
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('customer')}
              </label>
              <input
                required
                data-testid="customer-add-name"
                value={newDebt.name || ''}
                onChange={(e) => set({ name: e.target.value })}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('cust.add_account_number')}
              </label>
              <input
                data-testid="customer-add-number"
                value={newDebt.accountNumber || ''}
                onChange={(e) => set({ accountNumber: e.target.value })}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-mono"
                placeholder={t('cust.add_account_number_ph')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('phone')}
                </label>
                <input
                  value={newDebt.phone || ''}
                  onChange={(e) => set({ phone: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('address')}
                </label>
                <input
                  value={newDebt.address || ''}
                  onChange={(e) => set({ address: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('currency')}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => set({ currency: "IQD" })}
                    className={`flex-1 py-1.5 rounded-xl border font-bold text-xs transition-all ${newDebt.currency === "IQD" ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
                  >
                    {t('iqd')}
                  </button>
                  {!props.usdHidden && (
                    <button
                      type="button"
                      data-testid="customer-add-usd"
                      onClick={() => set({ currency: "USD" })}
                      className={`flex-1 py-1.5 rounded-xl border font-bold text-xs transition-all ${newDebt.currency === "USD" ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
                    >
                      {t('usd')}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('amount')}
                </label>
                <NumericInput
                  required
                  value={newDebt.amount ?? ''}
                  onChange={(val) => set({ amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-mono transition-all"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <CustomSelect
                  label={t('status')}
                  disabled={props.kindLocked}
                  options={[
                    { id: "debt", label: t('owed') },
                    { id: "trust", label: t('credit') },
                  ]}
                  value={newDebt.kind || "debt"}
                  onChange={(val) => set({ kind: val as CustomerKindKey })}
                  headerTitle={t('cust.add_status_header')}
                  searchable={false}
                  size="md"
                />
              </div>
              <div>
                <CustomSelect
                  label={t('cust.select_affected_box')}
                  options={props.boxOptions}
                  value={newDebt.boxId}
                  onChange={(val) => set({ boxId: String(val) })}
                  headerTitle={t('cust.boxes_header')}
                  placeholder={t('cust.select_box')}
                  searchPlaceholder={t('cust.search_box')}
                  searchable={true}
                  size="md"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('note')}
              </label>
              <input
                value={newDebt.note || ''}
                onChange={(e) => set({ note: e.target.value })}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
                placeholder={t('cust.optional_note')}
              />
            </div>
            {/* Payment Due Date Alert (تنبيه التسديد حسب التاريخ) */}
            <div className="space-y-2.5 bg-amber-500/5 dark:bg-amber-500/10 p-3.5 rounded-2xl border border-amber-500/20 dark:border-amber-500/30">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-tight">
                  <Bell className="w-4 h-4 text-amber-500 animate-pulse" />
                  <span>{t('cust.due_alert')}</span>
                </label>
                {newDebt.dueDate ? (
                  <span className="text-[10px] font-black bg-amber-500/20 text-amber-800 dark:text-amber-300 px-2.5 py-0.5 rounded-lg border border-amber-500/30 font-mono">
                    {t('cust.due_label', { date: newDebt.dueDate })}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500">
                    {t('cust.optional')}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <EnglishDatePicker
                  value={newDebt.dueDate || ""}
                  onChange={(val) => set({ dueDate: val })}
                  className="!py-2.5 !text-xs"
                />
                <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                  <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 shrink-0">
                    {t('cust.due_quick')}
                  </span>
                  <button
                    type="button"
                    onClick={() => props.onDueShortcut(7)}
                    className="px-2 py-1 text-[10px] font-bold bg-white dark:bg-[#161b22] hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-lg border border-amber-200/80 dark:border-amber-900/40 transition-all cursor-pointer shadow-xs active:scale-95"
                  >
                    +7 {t('cust.days_7')}
                  </button>
                  <button
                    type="button"
                    onClick={() => props.onDueShortcut(15)}
                    className="px-2 py-1 text-[10px] font-bold bg-white dark:bg-[#161b22] hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-lg border border-amber-200/80 dark:border-amber-900/40 transition-all cursor-pointer shadow-xs active:scale-95"
                  >
                    +15 {t('cust.days_n')}
                  </button>
                  <button
                    type="button"
                    onClick={() => props.onDueShortcut(30)}
                    className="px-2 py-1 text-[10px] font-bold bg-white dark:bg-[#161b22] hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-lg border border-amber-200/80 dark:border-amber-900/40 transition-all cursor-pointer shadow-xs active:scale-95"
                  >
                    +30 {t('cust.days_n')}
                  </button>
                  {newDebt.dueDate && (
                    <button
                      type="button"
                      onClick={() => set({ dueDate: "" })}
                      className="px-2 py-1 text-[10px] font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 rounded-lg border border-rose-200/80 dark:border-rose-900/40 hover:bg-rose-100 transition-all cursor-pointer shrink-0 mr-auto"
                    >
                      {t('cust.due_clear')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 border-t border-gray-100 dark:border-[#30363d] shrink-0">
            <button
              type="submit"
              data-testid="customer-add-submit"
              disabled={props.busy}
              style={{ backgroundColor: props.themeColor }}
              className="w-full py-3 text-white rounded-xl font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <span>💾</span>
              {t('save')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Same-name prompt (App.tsx:47840-48080)
// ---------------------------------------------------------------------------------------------------------------

export interface DuplicateNameMatch {
  readonly id: string;
  readonly name: string;
  readonly kindLabel: string;
  readonly accountNumber: string;
  readonly archived: boolean;
  /** Optional details of the old card ("رصيد الحساب الحالي", "الهاتف والعنوان"), preformatted. */
  readonly balanceLabel?: string;
  readonly phoneLabel?: string;
}

export interface DuplicateNameModalProps {
  readonly name: string;
  readonly matches: readonly DuplicateNameMatch[];
  readonly onViewStatement?: (id: string) => void;
  /** D-017: open the other kind (debt / trust) for the same person. */
  readonly onOpenForSamePerson?: (id: string) => void;
  /** D-072: restore an archived account with that name. */
  readonly onRestore?: (id: string) => void;
  readonly onChangeName: () => void;
  readonly onCancel: () => void;
}

export function DuplicateNameModal(props: DuplicateNameModalProps) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="duplicate-name-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onCancel}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-lg rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] max-h-[calc(100vh-2rem)] md:max-h-[90vh] flex flex-col text-right"
        dir="rtl"
      >
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-amber-50 dark:bg-amber-950/20 shrink-0">
          <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <span>⚠️</span>
            {t('cust.dup_title')}
          </h3>
          <button
            onClick={props.onCancel}
            className="text-gray-400 hover:text-gray-650 dark:hover:text-[#f0f6fc] transition-colors p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-[#30363d]"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          <p className="text-gray-650 dark:text-gray-300 text-sm leading-relaxed font-semibold">
            {t('cust.dup_text')}
          </p>
          {props.matches.map((m) => (
            <div key={m.id} data-testid={`duplicate-match-${m.id}`} className="bg-amber-50/40 dark:bg-amber-950/5 border border-amber-200 dark:border-amber-900/30 rounded-2xl p-4 sm:p-5 space-y-3">
              <h4 className="text-xs font-black text-amber-800 dark:text-amber-400 uppercase tracking-wider mb-2">
                {t('cust.dup_details')}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">
                    {t('cust.dup_full_name')}
                  </span>
                  <span className="font-bold text-gray-800 dark:text-white text-sm">
                    {m.name}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">
                    {t('cust.account_number')}
                  </span>
                  <span className="font-mono font-bold text-gray-800 dark:text-white text-sm">
                    {m.accountNumber || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">
                    {t('cust.dup_type')}
                  </span>
                  <span className="font-bold text-gray-850 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-lg inline-block">
                    {m.kindLabel}{m.archived ? ` (${t('cust.archived_badge')})` : ''}
                  </span>
                </div>
                {m.balanceLabel !== undefined && (
                  <div>
                    <span className="text-gray-400 dark:text-gray-500 block mb-1">
                      {t('cust.dup_balance')}
                    </span>
                    <span className="font-mono font-bold text-gray-800 dark:text-white text-sm">
                      {m.balanceLabel}
                    </span>
                  </div>
                )}
                {m.phoneLabel && (
                  <div className="col-span-2">
                    <span className="text-gray-400 dark:text-gray-500 block mb-1">
                      {t('cust.dup_phone_address')}
                    </span>
                    <span className="font-bold text-gray-800 dark:text-white text-sm">
                      {m.phoneLabel}
                    </span>
                  </div>
                )}
              </div>
              {(props.onRestore && m.archived) || (props.onOpenForSamePerson && !m.archived) ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  {props.onRestore && m.archived && (
                    <button
                      type="button"
                      data-testid={`duplicate-restore-${m.id}`}
                      onClick={() => props.onRestore?.(m.id)}
                      className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-1.5 text-xs"
                    >
                      <RotateCcw size={13} />
                      {t('cust.dup_restore')}
                    </button>
                  )}
                  {props.onOpenForSamePerson && !m.archived && (
                    <button
                      type="button"
                      data-testid={`duplicate-same-person-${m.id}`}
                      onClick={() => props.onOpenForSamePerson?.(m.id)}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-1.5 text-xs"
                    >
                      <Plus size={13} />
                      {t('cust.dup_same_person')}
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ))}
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('cust.dup_choose')}
          </div>
        </div>
        <div className="p-4 sm:p-5 border-t border-gray-100 dark:border-[#30363d] space-y-2 shrink-0">
          {props.onViewStatement && props.matches[0] && (
            <button
              type="button"
              data-testid="duplicate-view"
              onClick={() => props.onViewStatement?.(props.matches[0]!.id)}
              className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/10 text-sm"
            >
              <span>🔍</span>
              {t('cust.dup_view')}
            </button>
          )}
          <button
            type="button"
            data-testid="duplicate-change-name"
            onClick={props.onChangeName}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/10 text-sm"
          >
            <span>➕</span>
            {t('cust.dup_change_name')}
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-all text-xs"
          >
            {t('cust.dup_cancel')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Edit (App.tsx:34516-34623)
// ---------------------------------------------------------------------------------------------------------------

export interface EditCustomerValues {
  readonly name: string;
  readonly phone: string;
  readonly address: string;
  readonly accountNumber: string;
}

export interface EditCustomerModalProps {
  readonly values: EditCustomerValues;
  readonly onChange: (patch: Partial<EditCustomerValues>) => void;
  readonly themeColor: string;
  readonly busy?: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

export function EditCustomerModal(props: EditCustomerModalProps) {
  const { t } = useI18n();
  const editCustomerForm = props.values;
  const set = props.onChange;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-edit-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-50 dark:bg-[#161b22] shrink-0">
          <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow">
            <Edit2 className="text-blue-600 dark:text-blue-400" />
            {t('cust.edit_title')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!props.busy) props.onSubmit();
          }}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('customer')}
            </label>
            <input
              required
              data-testid="customer-edit-name"
              value={editCustomerForm.name || ''}
              onChange={(e) => set({ name: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('phone')}
            </label>
            <input
              value={editCustomerForm.phone || ''}
              onChange={(e) => set({ phone: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('address')}
            </label>
            <input
              value={editCustomerForm.address || ''}
              onChange={(e) => set({ address: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('cust.account_number')}
            </label>
            <input
              required
              value={editCustomerForm.accountNumber || ''}
              onChange={(e) => set({ accountNumber: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-mono"
            />
          </div>
          <button
            type="submit"
            data-testid="customer-edit-submit"
            style={{ backgroundColor: props.themeColor }}
            className="w-full py-4 text-white rounded-2xl font-black shadow-lg shadow-blue-600/20 hover:opacity-90 transition-all uppercase tracking-widest"
          >
            {t('save')}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Settle (App.tsx:34625-34710)
// ---------------------------------------------------------------------------------------------------------------

export interface CustomerSettleModalProps {
  readonly name: string;
  /** "1,000,000 د.ع | 50 $" (USD part only while USD is shown). */
  readonly balancesLabel: string;
  readonly boxValue: string;
  readonly onBoxChange: (id: string) => void;
  readonly boxOptions: readonly WalletSelectOption[];
  /** Shown under the box picker when the account holds USD: the USD part moves through the dollar vault (D-029). */
  readonly usdNote?: string | null;
  readonly note: string;
  readonly onNoteChange: (v: string) => void;
  readonly canConfirm: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

export function CustomerSettleModal(props: CustomerSettleModalProps) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4" data-testid="customer-settle-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#0d1117] rounded-3xl w-full max-w-md shadow-2xl border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[90vh] flex flex-col overflow-hidden text-right"
        dir="rtl"
      >
        <div className="shrink-0 border-b border-gray-100 dark:border-[#30363d] p-6 flex justify-between items-center bg-emerald-50 dark:bg-emerald-950/10 shrink-0">
          <h3 className="text-xl font-black dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
            {t('settle')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 pr-1">
          <p className="text-xs text-gray-650 dark:text-[#8b949e] font-bold leading-normal mt-1">
            {t('settleConfirm')}
            <br />
            <span className="font-black text-orange-600 dark:text-orange-400 font-mono dark:digital-glow block mt-1.5 text-sm">
              {props.name}:{" "}
              {props.balancesLabel}
            </span>
          </p>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('selectWallet')}
            </label>
            <WalletSelect value={props.boxValue} onChange={props.onBoxChange} options={props.boxOptions} required />
            {props.usdNote && (
              <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 mt-1.5" data-testid="customer-settle-usd-note">
                {props.usdNote}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('note')}
            </label>
            <input
              type="text"
              value={props.note}
              onChange={(e) => props.onNoteChange(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
              placeholder={t('cust.optional_note')}
            />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100 dark:border-[#30363d] shrink-0">
          <button
            onClick={props.onConfirm}
            data-testid="customer-settle-confirm"
            disabled={!props.canConfirm}
            className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-lg shadow-emerald-600/20"
          >
            {t('confirm')}
          </button>
          <button
            onClick={props.onClose}
            className="flex-1 bg-gray-100 dark:bg-[#05070a] text-gray-600 dark:text-[#8b949e] py-3 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-[#161b22] transition-all border border-gray-200 dark:border-[#30363d]"
          >
            {t('cancel')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Surplus options (App.tsx:34759-34950), shared by the adjustment and the transfer (D-073 (2))
// ---------------------------------------------------------------------------------------------------------------

export type SurplusChoice = 'same_customer' | 'select_existing_account' | 'create_new_account';

export interface SurplusOptionsProps {
  /** `debt_overpaid`: the excess goes to a trust account; `trust_overdrawn`: the excess becomes debt. */
  readonly type: 'debt_overpaid' | 'trust_overdrawn';
  readonly customerName: string;
  readonly choice: SurplusChoice;
  readonly onChoice: (c: SurplusChoice) => void;
  /** Accounts of the target kind in the same currency (label preformatted: "name (kind) - balance currency"). */
  readonly targets: readonly { readonly id: string; readonly label: string }[];
  readonly targetId: string;
  readonly onTarget: (id: string) => void;
  readonly newName: string;
  readonly onNewName: (v: string) => void;
}

export function SurplusOptions(props: SurplusOptionsProps) {
  const { t } = useI18n();
  const over = props.type === 'debt_overpaid';
  const surplusHandlingType = props.choice;
  return (
    <div className="space-y-3" data-testid="surplus-options">
      <label className="block text-xs font-black text-gray-400 dark:text-[#8b949e] uppercase tracking-wider">
        {over ? t('cust.surplus_where_over') : t('cust.surplus_where_trust')}
      </label>
      <div className="grid grid-cols-1 gap-2">
        <button
          type="button"
          data-testid="surplus-same"
          onClick={() => props.onChoice("same_customer")}
          className={`flex flex-col items-start p-3 rounded-xl border text-right transition-all ${
            surplusHandlingType === "same_customer"
              ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/10"
              : "border-gray-200 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#05070a]"
          }`}
        >
          <span className="font-black text-xs text-gray-800 dark:text-[#f0f6fc]">
            {t('cust.surplus_same', { name: props.customerName })}
          </span>
          <span className="text-[10px] text-gray-400 mt-1">
            {over ? t('cust.surplus_same_hint_over') : t('cust.surplus_same_hint_trust')}
          </span>
        </button>
        <button
          type="button"
          data-testid="surplus-existing"
          onClick={() => {
            props.onChoice("select_existing_account");
            const first = props.targets[0];
            if (first && !props.targetId) props.onTarget(first.id);
          }}
          className={`flex flex-col items-start p-3 rounded-xl border text-right transition-all ${
            surplusHandlingType === "select_existing_account"
              ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/10"
              : "border-gray-200 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#05070a]"
          }`}
        >
          <span className="font-black text-xs text-gray-800 dark:text-[#f0f6fc]">
            {over ? t('cust.surplus_existing_over') : t('cust.surplus_existing_trust')}
          </span>
          <span className="text-[10px] text-gray-400 mt-1">
            {t('cust.surplus_existing_hint')}
          </span>
        </button>
        <button
          type="button"
          data-testid="surplus-new"
          onClick={() => props.onChoice("create_new_account")}
          className={`flex flex-col items-start p-3 rounded-xl border text-right transition-all ${
            surplusHandlingType === "create_new_account"
              ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/10"
              : "border-gray-200 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#05070a]"
          }`}
        >
          <span className="font-black text-xs text-gray-800 dark:text-[#f0f6fc]">
            {over ? t('cust.surplus_new_over') : t('cust.surplus_new_trust')}
          </span>
          <span className="text-[10px] text-gray-400 mt-1">
            {t('cust.surplus_new_hint')}
          </span>
        </button>
      </div>
      {/* Existing Accounts selector */}
      {surplusHandlingType === "select_existing_account" && (
        <div className="mt-3">
          <label className="block text-xs font-bold text-gray-400 mb-1.5 uppercase tracking-widest">
            {t('cust.surplus_target')}
          </label>
          <select
            value={props.targetId}
            data-testid="surplus-target"
            onChange={(e) => props.onTarget(e.target.value)}
            className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc]"
          >
            <option value="">
              {t('cust.surplus_target_ph')}
            </option>
            {props.targets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Create New Account Field */}
      {surplusHandlingType === "create_new_account" && (
        <div className="mt-3">
          <label className="block text-xs font-bold text-gray-400 mb-1.5 uppercase tracking-widest">
            {t('cust.surplus_new_name')}
          </label>
          <input
            type="text"
            data-testid="surplus-new-name"
            value={props.newName}
            onChange={(e) => props.onNewName(e.target.value)}
            placeholder={t('cust.surplus_new_name_ph')}
            className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Adjustment (App.tsx:34712-35172)
// ---------------------------------------------------------------------------------------------------------------

export interface CustomerAdjustSurplus {
  readonly type: 'debt_overpaid' | 'trust_overdrawn';
  /** Preformatted with the old currency code suffix: "150,000 IQD". */
  readonly amountLabel: string;
  readonly originalLabel: string;
  readonly surplusLabel: string;
}

export interface CustomerAdjustModalProps {
  readonly name: string;
  readonly kind: CustomerKindKey;
  /** "1,000,000 د.ع | 50 $" (old `adjustingDebt.amount` + `amount_usd`). */
  readonly balancesLabel: string;
  readonly currency: CustomerCurrency;
  readonly onCurrencyChange: (c: CustomerCurrency) => void;
  readonly usdHidden: boolean;
  readonly action: 'add' | 'withdraw';
  readonly onActionChange: (a: 'add' | 'withdraw') => void;
  readonly amount: string;
  readonly onAmountChange: (v: string) => void;
  readonly boxValue: string;
  readonly onBoxChange: (id: string) => void;
  readonly boxOptions: readonly WalletSelectOption[];
  /** "متابعة أرصدة الحساب الحالية" from the server preview (null until a valid preview exists). */
  readonly live: { readonly beforeLabel: string; readonly amountLabel: string; readonly afterLabel: string } | null;
  readonly note: string;
  readonly onNoteChange: (v: string) => void;
  readonly canConfirm: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  /** Non-null = the surplus step (App.tsx:34720-34950). */
  readonly surplus: CustomerAdjustSurplus | null;
  readonly surplusProps: Omit<SurplusOptionsProps, 'type' | 'customerName'>;
  readonly onSurplusConfirm: () => void;
  readonly onSurplusBack: () => void;
  readonly busy?: boolean;
}

export function CustomerAdjustModal(props: CustomerAdjustModalProps) {
  const { t } = useI18n();
  const adjustAction = props.action;
  const adjustCurrency = props.currency;
  const isTrust = props.kind === 'trust';
  const surplusInfo = props.surplus;
  const isReduction = adjustAction === "withdraw";
  const titleLabel = isTrust ? (adjustAction === "add" ? t('addTrust') : t('withdrawTrust')) : adjustAction === "add" ? t('addDebt') : t('repayDebt');
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="customer-adjust-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#0d1117] rounded-3xl w-full max-w-md shadow-2xl border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[calc(100vh-2rem)] md:max-h-[85vh] flex flex-col overflow-hidden text-right"
        dir="rtl"
      >
        {surplusInfo ? (
          <>
            <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-orange-50 dark:bg-orange-950/10 shrink-0">
              <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
                <span>⚠️</span>
                {t('cust.surplus_title')}
              </h3>
              <button
                onClick={props.onClose}
                className="text-gray-400 hover:text-gray-650 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-orange-100/50 dark:hover:bg-orange-950/20"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 pr-1">
              <div className="p-4 bg-orange-50 dark:bg-orange-950/20 text-orange-800 dark:text-orange-400 rounded-2xl border border-orange-100 dark:border-orange-900/30">
                <p className="text-xs font-semibold leading-relaxed" data-testid="surplus-message">
                  {surplusInfo.type === "debt_overpaid"
                    ? t('cust.surplus_msg_over', { amount: surplusInfo.amountLabel, original: surplusInfo.originalLabel, surplus: surplusInfo.surplusLabel })
                    : t('cust.surplus_msg_trust', { amount: surplusInfo.amountLabel, original: surplusInfo.originalLabel, surplus: surplusInfo.surplusLabel })}
                </p>
              </div>
              <SurplusOptions type={surplusInfo.type} customerName={props.name} {...props.surplusProps} />
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-100 dark:border-[#30363d] shrink-0">
              <button
                onClick={props.onSurplusConfirm}
                disabled={props.busy}
                data-testid="surplus-confirm"
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
              >
                {t('cust.surplus_confirm')}
              </button>
              <button
                onClick={props.onSurplusBack}
                className="flex-1 bg-gray-100 dark:bg-[#05070a] text-gray-600 dark:text-[#8b949e] py-3 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-[#161b22] transition-all border border-gray-200 dark:border-[#30363d]"
              >
                {t('cust.back')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-50 dark:bg-[#161b22] shrink-0">
              <h3 className="text-lg sm:text-xl font-black dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
                {titleLabel}
              </h3>
              <button
                onClick={props.onClose}
                className="text-gray-400 hover:text-gray-650 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-150 dark:hover:bg-[#30363d]"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 pr-1">
              <p className="text-gray-600 dark:text-[#8b949e] mb-2 font-bold text-sm">
                {t('adjustConfirm')}
                <br />
                <span className="font-black text-blue-600 dark:text-blue-400 font-mono dark:digital-glow block mt-1.5">
                  {props.name}:{" "}
                  {props.balancesLabel}
                </span>
              </p>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('currency')}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => props.onCurrencyChange("IQD")}
                    className={`flex-1 py-2 rounded-xl border font-bold text-xs transition-all ${adjustCurrency === "IQD" ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
                  >
                    {t('iqd')}
                  </button>
                  {!props.usdHidden && (
                    <button
                      type="button"
                      data-testid="customer-adjust-usd"
                      onClick={() => props.onCurrencyChange("USD")}
                      className={`flex-1 py-2 rounded-xl border font-bold text-xs transition-all ${adjustCurrency === "USD" ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
                    >
                      {t('usd')}
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CustomSelect
                    label={t('actionType')}
                    options={[
                      { id: "add", label: isTrust ? t('addTrust') : t('addDebt') },
                      { id: "withdraw", label: isTrust ? t('withdrawTrust') : t('repayDebt') },
                    ]}
                    value={adjustAction}
                    onChange={(val) => props.onActionChange(val as 'add' | 'withdraw')}
                    headerTitle={t('cust.action_header')}
                    searchable={false}
                    size="md"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                    {t('amount')}
                  </label>
                  <NumericInput
                    value={props.amount}
                    onChange={(val) => props.onAmountChange(val)}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-mono"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <WalletSelect
                  value={props.boxValue}
                  onChange={props.onBoxChange}
                  options={props.boxOptions}
                  required
                  label={t('cust.select_affected_box')}
                  headerTitle={t('cust.boxes_header')}
                  placeholder={t('cust.select_box')}
                />
              </div>
              {/* Real-time Balance Before and After calculation */}
              {props.live && (
                <div className="bg-slate-50 dark:bg-[#0c1524] p-4 rounded-2xl border border-gray-200 dark:border-[#1f293d] space-y-2.5" data-testid="customer-adjust-live">
                  <h4 className="text-xs font-black text-slate-700 dark:text-blue-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-gray-200 dark:border-[#1f293d] pb-1.5">
                    <span>ℹ️</span>
                    {t('cust.live_title')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-right">
                    <div>
                      <span className="text-[10px] text-gray-400 dark:text-[#8b949e] font-bold block mb-0.5">
                        {t('cust.live_before')}
                      </span>
                      <span className="font-mono font-black text-sm text-slate-800 dark:text-gray-200">
                        {props.live.beforeLabel}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-400 dark:text-[#8b949e] font-bold block mb-0.5">
                        {t('cust.live_amount')}
                      </span>
                      <span className={`font-mono font-black text-sm ${isReduction ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                        {isReduction ? "-" : "+"}{" "}
                        {props.live.amountLabel}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-dashed border-gray-200 dark:border-[#1f293d] flex justify-between items-center text-right">
                    <span className="text-xs font-black text-slate-700 dark:text-gray-300">
                      {t('cust.live_after')}
                    </span>
                    <span className={`font-mono text-base font-black ${isReduction ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`} data-testid="customer-adjust-after">
                      {props.live.afterLabel}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                  {t('note')}
                </label>
                <input
                  type="text"
                  value={props.note}
                  onChange={(e) => props.onNoteChange(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all"
                  placeholder={t('cust.optional_note')}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-[#30363d] flex gap-3 shrink-0">
              <button
                onClick={props.onConfirm}
                data-testid="customer-adjust-confirm"
                disabled={!props.canConfirm || props.busy}
                className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 shadow-lg shadow-blue-600/20"
              >
                {t('confirm')}
              </button>
              <button
                onClick={props.onClose}
                className="flex-1 bg-gray-100 dark:bg-[#05070a] text-gray-600 dark:text-[#8b949e] py-3 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-[#161b22] transition-all border border-gray-200 dark:border-[#30363d]"
              >
                {t('cancel')}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Currency conversion on the account (App.tsx:35174-35368; D-070, D-071)
// ---------------------------------------------------------------------------------------------------------------

export type ConversionDirection = 'USD_to_IQD' | 'IQD_to_USD';

export interface CustomerConvertModalProps {
  readonly name: string;
  readonly kindLabel: string;
  readonly usdAvailableLabel: string;
  readonly iqdAvailableLabel: string;
  readonly direction: ConversionDirection;
  readonly onDirectionChange: (d: ConversionDirection) => void;
  /** USD amount in both directions (engine input `usd`, D-012). */
  readonly amount: string;
  readonly onAmountChange: (v: string) => void;
  readonly rate: string;
  readonly onRateChange: (v: string) => void;
  /** Sell-type only (office sells USD: trust IQD→USD, debt USD→IQD): the D-065 / D-239 cost; otherwise null. */
  readonly cost: string | null;
  readonly onCostChange: (v: string) => void;
  /** Server preview (D-071: "both new balances and, for sell-type, the profit" before confirmation). */
  readonly preview: { readonly iqdLabel: string; readonly usdAfterLabel: string; readonly iqdAfterLabel: string; readonly profitLabel: string | null } | null;
  readonly refusal: string | null;
  readonly note: string;
  readonly onNoteChange: (v: string) => void;
  readonly busy: boolean;
  readonly canConfirm: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

export function CustomerConvertModal(props: CustomerConvertModalProps) {
  const { t } = useI18n();
  const conversionType = props.direction;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4" data-testid="customer-convert-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#0d1117] rounded-3xl p-6 w-full max-w-lg shadow-2xl border border-gray-100 dark:border-[#30363d] dark:digital-border text-right"
        dir="rtl"
      >
        <div className="flex justify-between items-center pb-4 border-b border-gray-100 dark:border-[#30363d] mb-4">
          <h3 className="text-xl font-black text-slate-900 dark:text-[#f0f6fc] dark:digital-glow flex items-center gap-2">
            <ArrowLeftRight className="text-amber-500" size={20} />
            {t('cust.convert_modal_title')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="bg-slate-50 dark:bg-[#161b22]/50 p-4 rounded-2xl border border-gray-100 dark:border-[#30363d] mb-4">
          <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">
            {t('cust.convert_current')}
          </span>
          <span className="font-extrabold text-slate-800 dark:text-white text-base">
            {props.name}
            <span className="inline-block mr-2 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/25 text-indigo-700 dark:text-indigo-400 text-[10px] rounded-lg">
              {props.kindLabel}
            </span>
          </span>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="bg-white dark:bg-[#05070a] p-2.5 rounded-xl border border-gray-100 dark:border-[#30363d]/80 text-right">
              <span className="text-[10px] text-gray-400 font-bold block">
                {t('cust.convert_usd_available')}
              </span>
              <span className="font-mono font-black text-sm text-blue-600 dark:text-blue-400">
                {props.usdAvailableLabel}{" "}
                $
              </span>
            </div>
            <div className="bg-white dark:bg-[#05070a] p-2.5 rounded-xl border border-gray-100 dark:border-[#30363d]/80 text-right">
              <span className="text-[10px] text-gray-400 font-bold block">
                {t('cust.convert_iqd_available')}
              </span>
              <span className="font-mono font-black text-sm text-amber-600 dark:text-amber-400">
                {props.iqdAvailableLabel}{" "}
                د.ع
              </span>
            </div>
          </div>
        </div>
        <div className="space-y-4 text-right">
          {/* Direction Selection */}
          <div>
            <label className="block text-xs font-black text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wide">
              {t('cust.convert_direction')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="convert-usd-to-iqd"
                onClick={() => props.onDirectionChange("USD_to_IQD")}
                className={`flex-1 py-3 px-4 rounded-xl border font-black text-xs transition-with-shadow flex items-center justify-center gap-2 ${
                  conversionType === "USD_to_IQD"
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/20"
                    : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-450 border-gray-200 dark:border-[#30363d]"
                }`}
              >
                <span>$ ➔ د.ع</span>
                <span>{t('cust.convert_usd_to_iqd')}</span>
              </button>
              <button
                type="button"
                data-testid="convert-iqd-to-usd"
                onClick={() => props.onDirectionChange("IQD_to_USD")}
                className={`flex-1 py-3 px-4 rounded-xl border font-black text-xs transition-with-shadow flex items-center justify-center gap-2 ${
                  conversionType === "IQD_to_USD"
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/20"
                    : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-450 border-gray-200 dark:border-[#30363d]"
                }`}
              >
                <span>د.ع ➔ $</span>
                <span>{t('cust.convert_iqd_to_usd')}</span>
              </button>
            </div>
          </div>
          {/* Inputs Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wide">
                {t('cust.convert_amount_usd')}
              </label>
              <NumericInput
                value={props.amount}
                onChange={(val) => props.onAmountChange(val)}
                className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm font-mono dark:text-white"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wide">
                {t('cust.convert_rate')}
              </label>
              <NumericInput
                value={props.rate}
                onChange={(val) => props.onRateChange(val)}
                className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm font-mono dark:text-white"
                placeholder="0"
              />
            </div>
          </div>
          {props.cost !== null && (
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wide">
                {t('cust.convert_cost')}
              </label>
              <NumericInput
                value={props.cost}
                onChange={(val) => props.onCostChange(val)}
                className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm font-mono dark:text-white"
                placeholder="0"
              />
            </div>
          )}
          {props.preview && (
            <div className="bg-slate-50 dark:bg-[#0c1524] p-4 rounded-2xl border border-gray-200 dark:border-[#1f293d] space-y-2" data-testid="convert-preview">
              <div className="flex justify-between items-center text-xs font-bold text-slate-600 dark:text-slate-300">
                <span>{t('cust.convert_result')}</span>
                <span className="font-mono font-black text-amber-600 dark:text-amber-400">{props.preview.iqdLabel} د.ع</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-gray-400 font-bold block">{t('cust.convert_usd_after')}</span>
                  <span className="font-mono font-black text-sm text-blue-600 dark:text-blue-400" data-testid="convert-usd-after">{props.preview.usdAfterLabel} $</span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold block">{t('cust.convert_iqd_after')}</span>
                  <span className="font-mono font-black text-sm text-amber-600 dark:text-amber-400" data-testid="convert-iqd-after">{props.preview.iqdAfterLabel} د.ع</span>
                </div>
              </div>
              {props.preview.profitLabel !== null && (
                <div className="pt-2 border-t border-dashed border-gray-200 dark:border-[#1f293d] flex justify-between items-center text-xs font-black">
                  <span className="text-slate-700 dark:text-gray-300">{t('cust.convert_profit')}</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400" data-testid="convert-profit">{props.preview.profitLabel} د.ع</span>
                </div>
              )}
            </div>
          )}
          {props.refusal && (
            <p className="text-[11px] font-black text-rose-600 dark:text-rose-400" data-testid="convert-refusal">⚠️ {props.refusal}</p>
          )}
          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wide">
              {t('note')}
            </label>
            <input
              type="text"
              value={props.note}
              onChange={(e) => props.onNoteChange(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all duration-300 font-bold text-xs sm:text-sm dark:text-white"
              placeholder={t('cust.convert_note_ph')}
            />
          </div>
          {/* CTA */}
          <p className="text-[10px] font-black text-rose-600 dark:text-rose-400 text-center uppercase tracking-wider mb-2 flex items-center justify-center gap-1">
            <ShieldCheck size={12} />
            <span>{t('cust.convert_requires_pin')}</span>
          </p>
          <button
            type="button"
            data-testid="convert-confirm"
            onClick={props.onConfirm}
            disabled={!props.canConfirm || props.busy}
            className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-black py-3.5 rounded-2xl text-xs uppercase tracking-widest transition-all"
          >
            {props.busy ? t('processing') : t('cust.convert_submit')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Archive blocked — "settle first" (App.tsx:34271-34514 without the settle-and-delete option, D-072)
// ---------------------------------------------------------------------------------------------------------------

export interface ArchiveBlockedModalProps {
  readonly name: string;
  readonly iqdLabel: string;
  readonly usdLabel: string;
  readonly usdHidden: boolean;
  readonly onSettle: () => void;
  readonly onTransfer: () => void;
  readonly onClose: () => void;
}

export function ArchiveBlockedModal(props: ArchiveBlockedModalProps) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="archive-blocked-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-lg rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[90vh] flex flex-col text-right"
        dir="rtl"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-rose-50 dark:bg-rose-950/20 shrink-0">
          <h3 className="text-xl font-black flex items-center gap-2 text-rose-600 dark:text-rose-400 dark:digital-glow">
            <AlertTriangle className="animate-bounce" size={24} />
            <span>{t('cust.blocked_title')}</span>
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="p-4 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/30 rounded-2xl">
            <p className="text-sm font-bold text-gray-700 dark:text-gray-200 leading-relaxed">
              {t('cust.blocked_text', { name: props.name })}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-black text-gray-400 dark:text-[#8b949e] uppercase tracking-widest mb-3">
              {t('cust.blocked_balances')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl text-center">
                <span className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">
                  {t('cust.blocked_iqd')}
                </span>
                <span className="text-lg font-black text-orange-600 dark:text-orange-400 font-mono">
                  {props.iqdLabel}{" "}
                  د.ع
                </span>
              </div>
              {!props.usdHidden && (
                <div className="p-4 bg-gray-50 dark:bg-[#05070a] border border-gray-100 dark:border-[#30363d] rounded-2xl text-center">
                  <span className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1">
                    {t('cust.blocked_usd')}
                  </span>
                  <span className="text-lg font-black text-blue-600 dark:text-blue-400 font-mono">
                    {props.usdLabel}{" "}
                    $
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="text-xs font-black text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">
              {t('cust.blocked_options')}
            </h4>
            <div className="grid grid-cols-1 gap-4">
              {/* Option 1: ordinary settlement first (D-072 replaces "settle and delete") */}
              <button
                onClick={props.onSettle}
                data-testid="archive-blocked-settle"
                className="flex items-start gap-4 p-4 text-right bg-rose-50/30 hover:bg-rose-50/60 dark:bg-rose-950/10 dark:hover:bg-rose-950/20 border border-rose-200/60 dark:border-rose-900/40 rounded-2xl transition-all group cursor-pointer"
              >
                <div className="p-3 bg-rose-100 dark:bg-rose-900/50 rounded-xl text-rose-600 dark:text-rose-400 shrink-0 mt-0.5">
                  <Coins size={20} />
                </div>
                <div className="flex-1">
                  <h5 className="font-extrabold text-sm text-rose-800 dark:text-rose-300 group-hover:underline">
                    {t('cust.blocked_settle_title')}
                  </h5>
                  <p className="text-xs text-rose-600/90 dark:text-rose-400/85 mt-1 font-medium leading-relaxed font-semibold">
                    {t('cust.blocked_settle_text')}
                  </p>
                </div>
              </button>
              {/* Option 2: Transfer funds */}
              <button
                onClick={props.onTransfer}
                data-testid="archive-blocked-transfer"
                className="flex items-start gap-4 p-4 text-right bg-indigo-50 hover:bg-indigo-100/80 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/35 border border-indigo-200 dark:border-indigo-900/40 rounded-2xl transition-all group cursor-pointer"
              >
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/50 rounded-xl text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5">
                  <ArrowLeftRight size={20} />
                </div>
                <div className="flex-1">
                  <h5 className="font-extrabold text-sm text-indigo-800 dark:text-indigo-300 group-hover:underline">
                    {t('cust.blocked_transfer_title')}
                  </h5>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400/85 mt-1 font-medium leading-relaxed">
                    {t('cust.blocked_transfer_text')}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-100 dark:border-[#30363d] flex gap-3 bg-gray-50 dark:bg-[#161b22] shrink-0">
          <button
            onClick={props.onClose}
            className="w-full bg-gray-200 dark:bg-[#30363d] text-gray-750 dark:text-[#f0f6fc] py-3.5 rounded-xl font-black uppercase tracking-widest hover:opacity-90 transition-all border border-gray-300 dark:border-[#30363d] cursor-pointer"
          >
            {t('cust.close_window')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Archived accounts (D-072) — composed from the frames above (header of the edit dialog, rows of the table)
// ---------------------------------------------------------------------------------------------------------------

export interface ArchivedCustomerRow {
  readonly id: string;
  readonly name: string;
  readonly accountNumber: string;
  readonly kind: CustomerKindKey;
  readonly archivedLabel: string;
}

export interface ArchivedCustomersModalProps {
  readonly rows: readonly ArchivedCustomerRow[];
  readonly canRestore: boolean;
  readonly onStatement: (id: string) => void;
  readonly onRestore: (id: string) => void;
  readonly onClose: () => void;
}

export function ArchivedCustomersModal(props: ArchivedCustomersModalProps) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="archived-customers-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-2xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50 dark:bg-[#161b22] shrink-0">
          <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow">
            <Archive className="text-gray-600 dark:text-gray-400" />
            {t('cust.archived_title')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <table className="w-full text-right border-collapse">
            <thead>
              <tr className="text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('customer')}</th>
                <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('status')}</th>
                <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('cust.archived_at')}</th>
                <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {props.rows.map((d) => (
                <tr key={d.id} data-testid={`archived-row-${d.id}`} className="border-b border-gray-50 dark:border-[#30363d]">
                  <td className="py-4 font-black dark:text-[#f0f6fc]">
                    <div>{d.name}</div>
                    <div className="text-[10px] text-gray-500 dark:text-[#8b949e] font-mono font-extrabold bg-gray-100 dark:bg-gray-800/40 px-2 py-0.5 rounded w-max mt-1 border border-gray-200 dark:border-[#30363d]/30 inline-flex items-center gap-1">
                      <span>💳</span>
                      <span>{d.accountNumber}</span>
                    </div>
                  </td>
                  <td className="py-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${d.kind === "debt" ? "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400" : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"}`}>
                      {d.kind === "debt" ? t('owed') : t('credit')}
                    </span>
                  </td>
                  <td className="py-4 text-gray-400 dark:text-[#8b949e] text-xs font-bold font-mono">{d.archivedLabel}</td>
                  <td className="py-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => props.onStatement(d.id)}
                        className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors flex items-center gap-1.5 border border-indigo-100 dark:border-indigo-900/30"
                      >
                        <FileText size={14} />
                        {t('cust.statement_btn')}
                      </button>
                      {props.canRestore && (
                        <button
                          onClick={() => props.onRestore(d.id)}
                          data-testid={`archived-restore-${d.id}`}
                          className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20 flex items-center gap-1.5"
                        >
                          <RotateCcw size={14} />
                          {t('cust.restore')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {props.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-xs text-gray-400 font-bold">{t('cust.archived_empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
